import { useInfiniteQuery } from '@tanstack/react-query';

import { FilterValues } from '@/hooks/use-filter-values';
import { flattenInfinitePages, getNextPageParam } from '@/lib/constants/pagination';

import { getClientSummaries } from '../api/clients.api';
import {
  CLIENTS_FILTER_KEY_MANAGER,
  CLIENTS_FILTER_KEY_SEARCH,
  CLIENTS_FILTER_KEY_STATUS,
} from '../constants/clients-filter-keys';
import { mapClientStatusToDTO } from '../mappers/client-status.mapper';
import { ClientStatus } from '../types/client';

/**
 * Hook for fetching client summaries with pagination and filtering
 * Used in main clients table/list view
 */
export const useClientSummaries = ({
  filters,
  enabled = true,
}: {
  filters: FilterValues['values'];
  enabled?: boolean;
}) => {
  return useInfiniteQuery({
    queryKey: ['clients', 'summaries', filters],
    queryFn: ({ pageParam }) => {
      const apiFilters = mapClientFilters(filters);
      return getClientSummaries({ filter: apiFilters, pagination: { cursor: pageParam } });
    },
    getNextPageParam,
    initialPageParam: undefined as number | undefined,
    enabled,
    staleTime: 10 * 10 * 1000, // 10 minutes
    select: flattenInfinitePages,
  });
};

/**
 * Maps UI filter values to API filter format
 */
const mapClientFilters = (filters: FilterValues['values']) => {
  const apiFilters: Record<string, any> = {};

  if (filters[CLIENTS_FILTER_KEY_SEARCH]) {
    apiFilters.searchString = filters[CLIENTS_FILTER_KEY_SEARCH];
  }

  if (filters[CLIENTS_FILTER_KEY_STATUS]) {
    const statusArray = Array.isArray(filters[CLIENTS_FILTER_KEY_STATUS])
      ? filters[CLIENTS_FILTER_KEY_STATUS]
      : [filters[CLIENTS_FILTER_KEY_STATUS]];
    apiFilters.statuses = (statusArray as ClientStatus[]).map(s => mapClientStatusToDTO(s));
  }

  if (filters[CLIENTS_FILTER_KEY_MANAGER]) {
    const managerArray = Array.isArray(filters[CLIENTS_FILTER_KEY_MANAGER])
      ? filters[CLIENTS_FILTER_KEY_MANAGER]
      : [filters[CLIENTS_FILTER_KEY_MANAGER]];
    apiFilters.managerIds = (managerArray as string[]).map(id => Number(id));
  }

  return apiFilters;
};
